import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Slider } from "../components/ui/slider";
import { toast } from "sonner";
import { motion } from "framer-motion";
import axios from "axios";
import Layout from "../components/Layout";
import EditableText from "../components/EditableText";
import {
  Heart, Brain, Moon, Zap, Activity, TrendingUp, TrendingDown,
  Minus, AlertTriangle, Plus, Wind, Calendar, Trash2, ArrowRight
} from "lucide-react";
import { Link } from "react-router-dom";

const PAIN_LOCATIONS = ["None", "Back", "Neck", "Wrists", "Shoulders", "Hands", "Jaw/TMJ", "Feet", "Other"];

const SliderField = ({ label, icon: Icon, value, onChange, min = 1, max = 10, color = "#8DB5A0", lowLabel, highLabel }) => (
  <div className="space-y-2">
    <div className="flex items-center justify-between">
      <Label className="flex items-center gap-2 text-sm text-[#6B4E8A]">
        {Icon && <Icon className="w-4 h-4" style={{ color }} />}
        {label}
      </Label>
      <span className="text-sm font-medium px-2 py-0.5 rounded-full" style={{ backgroundColor: `${color}20`, color }}>
        {value}
      </span>
    </div>
    <div className="flex items-center gap-3">
      <button
        type="button"
        onClick={() => onChange(Math.max(min, value - 1))}
        className="w-8 h-8 rounded-full border border-[#E5DFEC] flex items-center justify-center text-[#64748B] hover:bg-[#F0ECF7] shrink-0 active:bg-[#E5DFEC]"
        data-testid={`slider-minus-${label.toLowerCase().replace(/\s+/g, '-')}`}
      >
        -
      </button>
      <Slider
        value={[value]}
        onValueChange={([v]) => onChange(v)}
        min={min}
        max={max}
        step={1}
        className="flex-1"
      />
      <button
        type="button"
        onClick={() => onChange(Math.min(max, value + 1))}
        className="w-8 h-8 rounded-full border border-[#E5DFEC] flex items-center justify-center text-[#64748B] hover:bg-[#F0ECF7] shrink-0 active:bg-[#E5DFEC]"
        data-testid={`slider-plus-${label.toLowerCase().replace(/\s+/g, '-')}`}
      >
        +
      </button>
    </div>
    {(lowLabel || highLabel) && (
      <div className="flex justify-between text-xs text-[#64748B] px-11">
        <span>{lowLabel}</span>
        <span>{highLabel}</span>
      </div>
    )}
  </div>
);

const HealthTrackerPage = () => {
  const { user } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [checkins, setCheckins] = useState([]);
  const [averages, setAverages] = useState({});
  const [burnoutAssessments, setBurnoutAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCheckin, setShowCheckin] = useState(false);
  const [showBurnout, setShowBurnout] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [checkinForm, setCheckinForm] = useState({
    date: new Date().toISOString().split("T")[0],
    energy_level: 5,
    stress_level: 5,
    sleep_hours: 7,
    sleep_quality: 5,
    physical_pain: 0,
    pain_location: "",
    mood: 5,
    work_life_balance: 5,
    motivation: 5,
    notes: ""
  });

  const [burnoutForm, setBurnoutForm] = useState({
    emotional_exhaustion: 3,
    depersonalization: 3,
    personal_accomplishment: 3,
    work_overload: 3,
    lack_of_control: 3,
    insufficient_reward: 3,
    breakdown_of_community: 3,
    absence_of_fairness: 3,
    value_conflict: 3,
    notes: ""
  });

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [dashRes, checkinsRes, burnoutRes] = await Promise.all([
        axios.get(`${API}/health/dashboard`),
        axios.get(`${API}/health/checkins?days=30`),
        axios.get(`${API}/health/burnout-assessments?limit=5`)
      ]);
      setDashboard(dashRes.data);
      setCheckins(checkinsRes.data.checkins);
      setAverages(checkinsRes.data.averages);
      setBurnoutAssessments(burnoutRes.data);
    } catch (error) {
      toast.error("Failed to load health data");
    } finally {
      setLoading(false);
    }
  };

  const handleCheckin = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/health/checkin`, {
        ...checkinForm,
        sleep_hours: parseFloat(checkinForm.sleep_hours)
      });
      toast.success("Check-in recorded!");
      setShowCheckin(false);
      setCheckinForm({
        date: new Date().toISOString().split("T")[0],
        energy_level: 5, stress_level: 5, sleep_hours: 7, sleep_quality: 5,
        physical_pain: 0, pain_location: "", mood: 5, work_life_balance: 5,
        motivation: 5, notes: ""
      });
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save check-in");
    } finally {
      setSubmitting(false);
    }
  };

  const handleBurnoutAssessment = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/health/burnout-assessment`, burnoutForm);
      toast.success("Burnout assessment saved!");
      setShowBurnout(false);
      setBurnoutForm({
        emotional_exhaustion: 3, depersonalization: 3, personal_accomplishment: 3,
        work_overload: 3, lack_of_control: 3, insufficient_reward: 3,
        breakdown_of_community: 3, absence_of_fairness: 3, value_conflict: 3, notes: ""
      });
      fetchAll();
    } catch (error) {
      toast.error("Failed to save assessment");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteCheckin = async (checkinId) => {
    if (!window.confirm("Delete this check-in?")) return;
    try {
      await axios.delete(`${API}/health/checkin/${checkinId}`);
      toast.success("Check-in deleted");
      fetchAll();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const getTrendIcon = (trend) => {
    if (trend === "improving") return <TrendingUp className="w-4 h-4 text-[#10B981]" />;
    if (trend === "declining") return <TrendingDown className="w-4 h-4 text-[#EF4444]" />;
    return <Minus className="w-4 h-4 text-[#64748B]" />;
  };

  const getRiskColor = (risk) => {
    if (risk === "low") return "text-[#10B981] bg-[#10B981]/10";
    if (risk === "moderate") return "text-[#8DB5A0] bg-[#8DB5A0]/10";
    if (risk === "high") return "text-[#8DB5A0] bg-[#8DB5A0]/10";
    return "text-[#EF4444] bg-[#EF4444]/10";
  };

  const getWellnessColor = (score) => {
    if (score >= 7) return "#10B981";
    if (score >= 5) return "#8DB5A0";
    if (score >= 3) return "#8DB5A0";
    return "#EF4444";
  };

  return (
    <Layout>
      <div className="p-6 md:p-8 lg:p-12" data-testid="health-tracker-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <EditableText
              blockKey="health.title"
              fallback="Health & Wellness"
              as="h1"
              className="text-2xl sm:text-3xl font-medium text-[#6B4E8A]"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            />
            <EditableText
              blockKey="health.subtitle"
              fallback="Track your wellbeing, stress, and burnout risk"
              as="p"
              className="text-[#64748B] mt-1"
              multiline
            />
          </div>
          <div className="flex gap-3">
            <Dialog open={showCheckin} onOpenChange={setShowCheckin}>
              <DialogTrigger asChild>
                <Button className="bg-[#6B4E8A] text-white hover:bg-[#553D6F] rounded-full" data-testid="daily-checkin-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Daily Check-in
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Daily Wellness Check-in</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCheckin} className="space-y-5 mt-4">
                  <div>
                    <Label htmlFor="checkin-date">Date</Label>
                    <Input
                      id="checkin-date"
                      type="date"
                      value={checkinForm.date}
                      onChange={(e) => setCheckinForm({ ...checkinForm, date: e.target.value })}
                      className="mt-2 bg-white border-[#E5DFEC]"
                      data-testid="checkin-date-input"
                    />
                  </div>
                  <SliderField label="Energy Level" icon={Zap} value={checkinForm.energy_level} onChange={(v) => setCheckinForm({ ...checkinForm, energy_level: v })} color="#8DB5A0" lowLabel="Exhausted" highLabel="Energized" />
                  <SliderField label="Stress Level" icon={Activity} value={checkinForm.stress_level} onChange={(v) => setCheckinForm({ ...checkinForm, stress_level: v })} color="#EF4444" lowLabel="Calm" highLabel="Very stressed" />
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Sleep Hours</Label>
                      <Input
                        type="number"
                        step="0.5"
                        min="0"
                        max="24"
                        value={checkinForm.sleep_hours}
                        onChange={(e) => setCheckinForm({ ...checkinForm, sleep_hours: e.target.value })}
                        className="mt-2 bg-white border-[#E5DFEC]"
                        data-testid="sleep-hours-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <SliderField label="Sleep Quality" icon={Moon} value={checkinForm.sleep_quality} onChange={(v) => setCheckinForm({ ...checkinForm, sleep_quality: v })} color="#6366F1" lowLabel="Poor" highLabel="Excellent" />
                    </div>
                  </div>
                  <SliderField label="Physical Pain" icon={Heart} value={checkinForm.physical_pain} onChange={(v) => setCheckinForm({ ...checkinForm, physical_pain: v })} min={0} color="#8DB5A0" lowLabel="No pain" highLabel="Severe" />
                  {checkinForm.physical_pain > 0 && (
                    <div>
                      <Label>Pain Location</Label>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {PAIN_LOCATIONS.filter(l => l !== "None").map((loc) => (
                          <button
                            key={loc}
                            type="button"
                            onClick={() => setCheckinForm({ ...checkinForm, pain_location: loc.toLowerCase() })}
                            className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                              checkinForm.pain_location === loc.toLowerCase()
                                ? "bg-[#6B4E8A] text-white border-[#6B4E8A]"
                                : "border-[#E5DFEC] text-[#64748B] hover:border-[#6B4E8A]"
                            }`}
                          >
                            {loc}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  <SliderField label="Mood" icon={Heart} value={checkinForm.mood} onChange={(v) => setCheckinForm({ ...checkinForm, mood: v })} color="#F0ECF7" lowLabel="Low" highLabel="Great" />
                  <SliderField label="Work-Life Balance" icon={Activity} value={checkinForm.work_life_balance} onChange={(v) => setCheckinForm({ ...checkinForm, work_life_balance: v })} color="#10B981" lowLabel="Poor" highLabel="Excellent" />
                  <SliderField label="Motivation" icon={Zap} value={checkinForm.motivation} onChange={(v) => setCheckinForm({ ...checkinForm, motivation: v })} color="#8DB5A0" lowLabel="None" highLabel="Highly motivated" />
                  <div>
                    <Label>Notes (optional)</Label>
                    <Textarea
                      value={checkinForm.notes}
                      onChange={(e) => setCheckinForm({ ...checkinForm, notes: e.target.value })}
                      placeholder="How are you feeling today?"
                      className="mt-2 bg-white border-[#E5DFEC]"
                    />
                  </div>
                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setShowCheckin(false)} className="rounded-full">Cancel</Button>
                    <Button type="submit" className="bg-[#10B981] text-white hover:bg-[#059669] rounded-full" disabled={submitting} data-testid="submit-checkin-btn">
                      {submitting ? "Saving..." : "Save Check-in"}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>

            <Dialog open={showBurnout} onOpenChange={setShowBurnout}>
              <DialogTrigger asChild>
                <Button variant="outline" className="border-[#8DB5A0] text-[#8DB5A0] hover:bg-[#8DB5A0]/10 rounded-full" data-testid="burnout-assessment-btn">
                  <Brain className="w-4 h-4 mr-2" />
                  Burnout Assessment
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Burnout Risk Assessment</DialogTitle>
                </DialogHeader>
                <p className="text-sm text-[#64748B] mt-2">
                  Rate each area from 1 (strongly disagree) to 5 (strongly agree). Be honest for the most accurate results.
                </p>
                <form onSubmit={handleBurnoutAssessment} className="space-y-5 mt-4">
                  <SliderField label="I feel emotionally drained from work" value={burnoutForm.emotional_exhaustion} onChange={(v) => setBurnoutForm({ ...burnoutForm, emotional_exhaustion: v })} min={1} max={5} color="#EF4444" lowLabel="Disagree" highLabel="Agree" />
                  <SliderField label="I feel detached from my patients/colleagues" value={burnoutForm.depersonalization} onChange={(v) => setBurnoutForm({ ...burnoutForm, depersonalization: v })} min={1} max={5} color="#8DB5A0" lowLabel="Disagree" highLabel="Agree" />
                  <SliderField label="I feel personally accomplished in my work" value={burnoutForm.personal_accomplishment} onChange={(v) => setBurnoutForm({ ...burnoutForm, personal_accomplishment: v })} min={1} max={5} color="#10B981" lowLabel="Disagree" highLabel="Agree" />
                  <SliderField label="I feel overwhelmed by my workload" value={burnoutForm.work_overload} onChange={(v) => setBurnoutForm({ ...burnoutForm, work_overload: v })} min={1} max={5} color="#EF4444" lowLabel="Disagree" highLabel="Agree" />
                  <SliderField label="I feel I lack control over my work" value={burnoutForm.lack_of_control} onChange={(v) => setBurnoutForm({ ...burnoutForm, lack_of_control: v })} min={1} max={5} color="#8DB5A0" lowLabel="Disagree" highLabel="Agree" />
                  <SliderField label="I feel insufficiently rewarded" value={burnoutForm.insufficient_reward} onChange={(v) => setBurnoutForm({ ...burnoutForm, insufficient_reward: v })} min={1} max={5} color="#8DB5A0" lowLabel="Disagree" highLabel="Agree" />
                  <SliderField label="I feel disconnected from my community" value={burnoutForm.breakdown_of_community} onChange={(v) => setBurnoutForm({ ...burnoutForm, breakdown_of_community: v })} min={1} max={5} color="#8DB5A0" lowLabel="Disagree" highLabel="Agree" />
                  <SliderField label="I feel treated unfairly at work" value={burnoutForm.absence_of_fairness} onChange={(v) => setBurnoutForm({ ...burnoutForm, absence_of_fairness: v })} min={1} max={5} color="#8DB5A0" lowLabel="Disagree" highLabel="Agree" />
                  <SliderField label="My work conflicts with my personal values" value={burnoutForm.value_conflict} onChange={(v) => setBurnoutForm({ ...burnoutForm, value_conflict: v })} min={1} max={5} color="#EF4444" lowLabel="Disagree" highLabel="Agree" />
                  <div>
                    <Label>Additional Notes (optional)</Label>
                    <Textarea
                      value={burnoutForm.notes}
                      onChange={(e) => setBurnoutForm({ ...burnoutForm, notes: e.target.value })}
                      placeholder="Any additional thoughts..."
                      className="mt-2 bg-white border-[#E5DFEC]"
                    />
                  </div>
                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setShowBurnout(false)} className="rounded-full">Cancel</Button>
                    <Button type="submit" className="bg-[#8DB5A0] text-white hover:bg-[#D0694E] rounded-full" disabled={submitting} data-testid="submit-burnout-btn">
                      {submitting ? "Saving..." : "Submit Assessment"}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {loading ? (
          <div className="space-y-6">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-32 bg-[#F0ECF7] rounded-2xl animate-pulse"></div>
            ))}
          </div>
        ) : (
          <>
            {/* Dashboard Overview */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="bg-white border border-[#E5DFEC] rounded-2xl p-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${getWellnessColor(averages.wellness_score || 0)}20` }}>
                    <Heart className="w-5 h-5" style={{ color: getWellnessColor(averages.wellness_score || 0) }} />
                  </div>
                  <div>
                    <p className="text-2xl font-medium text-[#6B4E8A]">{averages.wellness_score || "—"}</p>
                    <p className="text-xs text-[#64748B]">Wellness Score</p>
                  </div>
                </div>
              </div>
              <div className="bg-white border border-[#E5DFEC] rounded-2xl p-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#EF4444]/10 flex items-center justify-center">
                    <Activity className="w-5 h-5 text-[#EF4444]" />
                  </div>
                  <div>
                    <p className="text-2xl font-medium text-[#6B4E8A]">{averages.stress_level || "—"}</p>
                    <p className="text-xs text-[#64748B]">Avg Stress</p>
                  </div>
                </div>
              </div>
              <div className="bg-white border border-[#E5DFEC] rounded-2xl p-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#6366F1]/10 flex items-center justify-center">
                    <Moon className="w-5 h-5 text-[#6366F1]" />
                  </div>
                  <div>
                    <p className="text-2xl font-medium text-[#6B4E8A]">{averages.sleep_hours || "—"}h</p>
                    <p className="text-xs text-[#64748B]">Avg Sleep</p>
                  </div>
                </div>
              </div>
              <div className="bg-white border border-[#E5DFEC] rounded-2xl p-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#8DB5A0]/10 flex items-center justify-center">
                    {getTrendIcon(dashboard?.trend)}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-[#6B4E8A] capitalize">{dashboard?.trend?.replace("_", " ") || "—"}</p>
                    <p className="text-xs text-[#64748B]">7-Day Trend</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Recommendations */}
            {dashboard?.recommendations?.length > 0 && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="mb-8 space-y-3">
                {dashboard.recommendations.map((rec, idx) => (
                  <div key={`${rec.code || rec.message?.slice(0, 32)}-${idx}`} className="flex items-start gap-3 bg-[#8DB5A0]/10 border border-[#8DB5A0]/30 rounded-xl p-4">
                    <AlertTriangle className="w-5 h-5 text-[#8DB5A0] mt-0.5 shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm text-[#6B4E8A]">{rec.message}</p>
                      {rec.type === "stress" && (
                        <Link to="/breathing" className="inline-flex items-center gap-1 text-sm text-[#8DB5A0] hover:text-[#D0694E] mt-2">
                          <Wind className="w-4 h-4" /> Try Box Breathing <ArrowRight className="w-3 h-3" />
                        </Link>
                      )}
                    </div>
                  </div>
                ))}
              </motion.div>
            )}

            <Tabs defaultValue="checkins" className="space-y-6">
              <TabsList className="bg-[#F0ECF7]">
                <TabsTrigger value="checkins" className="data-[state=active]:bg-white" data-testid="checkins-tab">
                  Check-ins ({checkins.length})
                </TabsTrigger>
                <TabsTrigger value="burnout" className="data-[state=active]:bg-white" data-testid="burnout-tab">
                  Burnout Assessments ({burnoutAssessments.length})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="checkins">
                {checkins.length > 0 ? (
                  <div className="space-y-3">
                    {checkins.map((checkin, idx) => (
                      <motion.div
                        key={checkin.checkin_id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.03 }}
                        className="bg-white border border-[#E5DFEC] rounded-2xl p-5"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${getWellnessColor(checkin.wellness_score)}20` }}>
                              <Heart className="w-5 h-5" style={{ color: getWellnessColor(checkin.wellness_score) }} />
                            </div>
                            <div>
                              <p className="font-medium text-[#6B4E8A]">
                                {new Date(checkin.date).toLocaleDateString("en-CA", { weekday: "short", month: "short", day: "numeric" })}
                              </p>
                              <p className="text-sm text-[#64748B]">Wellness: {checkin.wellness_score}/10</p>
                            </div>
                          </div>
                          <button onClick={() => handleDeleteCheckin(checkin.checkin_id)} className="p-2 hover:bg-[#EF4444]/10 rounded-lg">
                            <Trash2 className="w-4 h-4 text-[#EF4444]" />
                          </button>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                          <div className="flex items-center gap-2">
                            <Zap className="w-3.5 h-3.5 text-[#8DB5A0]" />
                            <span className="text-[#64748B]">Energy: {checkin.energy_level}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Activity className="w-3.5 h-3.5 text-[#EF4444]" />
                            <span className="text-[#64748B]">Stress: {checkin.stress_level}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Moon className="w-3.5 h-3.5 text-[#6366F1]" />
                            <span className="text-[#64748B]">Sleep: {checkin.sleep_hours}h</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Heart className="w-3.5 h-3.5 text-[#F0ECF7]" />
                            <span className="text-[#64748B]">Mood: {checkin.mood}</span>
                          </div>
                        </div>
                        {checkin.notes && <p className="text-sm text-[#64748B] mt-3 bg-[#FAF7F2] p-3 rounded-lg">{checkin.notes}</p>}
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-16 bg-white border border-[#E5DFEC] rounded-2xl">
                    <Heart className="w-12 h-12 mx-auto mb-4 text-[#E5DFEC]" />
                    <h3 className="text-lg font-medium text-[#6B4E8A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>No check-ins yet</h3>
                    <p className="text-[#64748B] mb-4">Start tracking your daily wellness</p>
                    <Button onClick={() => setShowCheckin(true)} className="bg-[#8DB5A0] text-white hover:bg-[#D0694E] rounded-full">
                      <Plus className="w-4 h-4 mr-2" /> Daily Check-in
                    </Button>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="burnout">
                {/* Latest Burnout Summary */}
                {dashboard?.latest_burnout && (
                  <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-6 bg-white border border-[#E5DFEC] rounded-2xl p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-medium text-[#6B4E8A]" style={{ fontFamily: 'Outfit, sans-serif' }}>Latest Assessment</h3>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getRiskColor(dashboard.latest_burnout.risk_level)}`}>
                        {dashboard.latest_burnout.risk_level?.charAt(0).toUpperCase() + dashboard.latest_burnout.risk_level?.slice(1)} Risk
                      </span>
                    </div>
                    <div className="w-full bg-[#F0ECF7] rounded-full h-3">
                      <div
                        className="h-3 rounded-full transition-all duration-500"
                        style={{
                          width: `${(dashboard.latest_burnout.burnout_score / 5) * 100}%`,
                          backgroundColor: dashboard.latest_burnout.risk_level === "low" ? "#10B981" : dashboard.latest_burnout.risk_level === "moderate" ? "#8DB5A0" : "#EF4444"
                        }}
                      ></div>
                    </div>
                    <p className="text-sm text-[#64748B] mt-2">Score: {dashboard.latest_burnout.burnout_score}/5</p>
                  </motion.div>
                )}

                {burnoutAssessments.length > 0 ? (
                  <div className="space-y-3">
                    {burnoutAssessments.map((assessment, idx) => (
                      <motion.div
                        key={assessment.assessment_id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.05 }}
                        className="bg-white border border-[#E5DFEC] rounded-2xl p-5"
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-[#8DB5A0]/10 flex items-center justify-center">
                              <Brain className="w-5 h-5 text-[#8DB5A0]" />
                            </div>
                            <div>
                              <p className="font-medium text-[#6B4E8A]">
                                {new Date(assessment.date).toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" })}
                              </p>
                              <p className="text-sm text-[#64748B]">Score: {assessment.burnout_score}/5</p>
                            </div>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${getRiskColor(assessment.risk_level)}`}>
                            {assessment.risk_level?.charAt(0).toUpperCase() + assessment.risk_level?.slice(1)}
                          </span>
                        </div>
                        {assessment.notes && <p className="text-sm text-[#64748B] bg-[#FAF7F2] p-3 rounded-lg">{assessment.notes}</p>}
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-16 bg-white border border-[#E5DFEC] rounded-2xl">
                    <Brain className="w-12 h-12 mx-auto mb-4 text-[#E5DFEC]" />
                    <h3 className="text-lg font-medium text-[#6B4E8A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>No assessments yet</h3>
                    <p className="text-[#64748B] mb-4">Take a burnout risk assessment</p>
                    <Button onClick={() => setShowBurnout(true)} className="bg-[#8DB5A0] text-white hover:bg-[#D0694E] rounded-full">
                      <Brain className="w-4 h-4 mr-2" /> Take Assessment
                    </Button>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </>
        )}
      </div>
    </Layout>
  );
};

export default HealthTrackerPage;
